import React, { useState } from "react";
import moment from "moment";

const ACCENT = "#4f3df5";

const formatTime = (t) => (t ? String(t).slice(0, 5) : "--:--");
const formatINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export const getDurationMinutes = (fare) => {
  const dep = moment(`${fare.origin?.date} ${fare.origin?.time}`, "YYYY-MM-DD HH:mm:ss");
  const arr = moment(`${fare.destination?.date} ${fare.destination?.time}`, "YYYY-MM-DD HH:mm:ss");
  const mins = arr.diff(dep, "minutes");
  return Number.isFinite(mins) && mins > 0 ? mins : 0;
};

// Total price for the searched passengers (per-pax fares from the API x pax count)
export const getPaxTotal = (fare, pax) => {
  const f = fare.fare || {};
  return (
    (f.adult?.total_fare || 0) * (pax?.adults || 0) +
    (f.child?.total_fare || 0) * (pax?.children || 0) +
    (f.infant?.total_fare || 0) * (pax?.infants || 0)
  );
};

const formatDuration = (mins) => (mins ? `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m` : "-");

const FlightResultCard = ({ fare, pax }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const duration = getDurationMinutes(fare);
  const total = getPaxTotal(fare, pax);
  const stops = Number(fare.flight_stops || 0);
  const dayShift = moment(fare.destination?.date).diff(moment(fare.origin?.date), "days");

  const breakupRows = [
    { key: "adult", label: "Adult", count: pax?.adults || 0 },
    { key: "child", label: "Child", count: pax?.children || 0 },
    { key: "infant", label: "Infant", count: pax?.infants || 0 },
  ].filter((r) => r.count > 0);

  return (
    <div className="fr-card">
      <div className="fr-main">
        {/* Airline */}
        <div className="fr-airline">
          {fare.airline?.logo && !logoError ? (
            <img src={fare.airline.logo} alt={fare.airline?.airline_code} className="fr-logo" onError={() => setLogoError(true)} />
          ) : (
            <div className="fr-logo fr-logo-fallback">{fare.airline?.airline_code}</div>
          )}
          <div className="min-w-0">
            <div className="fr-airline-name text-truncate">{fare.airline?.airline_name}</div>
            <div className="fr-muted">{fare.flight_number}</div>
          </div>
        </div>

        {/* Timeline */}
        <div className="fr-timeline">
          <div className="text-start">
            <div className="fr-time">{formatTime(fare.origin?.time)}</div>
            <div className="fr-city">{fare.origin?.city_name} ({fare.origin?.airport_code})</div>
            {fare.origin?.terminal && <div className="fr-muted">Terminal {fare.origin.terminal}</div>}
          </div>

          <div className="fr-path">
            <div className="fr-muted">{formatDuration(duration)}</div>
            <div className="fr-line">
              <span className="fr-line-dot" />
              {stops > 0 && <span className="fr-line-stop" />}
              <i className="bi bi-airplane-fill fr-line-plane"></i>
            </div>
            <div className="fr-stops" style={{ color: stops == 0 ? "#16a34a" : ACCENT }}>
              {stops == 0 ? "Non Stop" : `${stops} Stop${stops > 1 ? "s" : ""}`}
            </div>
          </div>

          <div className="text-end">
            <div className="fr-time">
              {formatTime(fare.destination?.time)}
              {dayShift > 0 && <sup className="fr-dayshift">+{dayShift}</sup>}
            </div>
            <div className="fr-city">{fare.destination?.city_name} ({fare.destination?.airport_code})</div>
            {fare.destination?.terminal && <div className="fr-muted">Terminal {fare.destination.terminal}</div>}
          </div>
        </div>

        {/* Price */}
        <div className="fr-price-col">
          <div className="fr-price">{formatINR(total)}</div>
          <div className="fr-muted">
            {formatINR(fare.fare?.adult?.total_fare)} / adult
          </div>
          <button type="button" className="fr-details-btn" onClick={() => setShowDetails(!showDetails)}>
            {showDetails ? "Hide Details" : "Fare Details"}
            <i className={`bi bi-chevron-${showDetails ? "up" : "down"} ms-1`}></i>
          </button>
        </div>
      </div>

      {/* Tags */}
      <div className="fr-tags">
        <span className={`fr-tag ${fare.is_refundable ? "fr-tag-green" : "fr-tag-red"}`}>
          <i className={`bi ${fare.is_refundable ? "bi-check-circle" : "bi-x-circle"} me-1`}></i>
          {fare.is_refundable ? "Refundable" : "Non-Refundable"}
        </span>
        {fare.baggage_policy && (
          <span className="fr-tag">
            <i className="bi bi-suitcase2 me-1"></i>
            {fare.baggage_policy}
          </span>
        )}
        <span className="fr-tag text-capitalize">
          <i className="bi bi-person-badge me-1"></i>
          {fare.cabin_class} ({fare.rbd})
        </span>
        {fare.available_seats != null && (
          <span className={`fr-tag ${fare.available_seats <= 5 ? "fr-tag-orange" : ""}`}>
            <i className="bi bi-grid-3x3-gap me-1"></i>
            {fare.available_seats} seat{fare.available_seats == 1 ? "" : "s"} left
          </span>
        )}
      </div>

      {showDetails && (
        <div className="fr-details">
          <div className="row g-3">
            <div className="col-12 col-lg-7">
              <div className="fr-detail-title">Fare Breakup</div>
              <div className="table-responsive">
                <table className="table table-sm mb-0 fr-table">
                  <thead>
                    <tr>
                      <th>Pax</th>
                      <th className="text-end">Base</th>
                      <th className="text-end">Tax</th>
                      <th className="text-end">Markup</th>
                      <th className="text-end">Per Pax</th>
                      <th className="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakupRows.map((r) => {
                      const f = fare.fare?.[r.key] || {};
                      return (
                        <tr key={r.key}>
                          <td>{r.label} x {r.count}</td>
                          <td className="text-end">{formatINR(f.base_fare)}</td>
                          <td className="text-end">{formatINR(f.tax_amount)}</td>
                          <td className="text-end">{formatINR(f.markup_amount)}</td>
                          <td className="text-end">{formatINR(f.total_fare)}</td>
                          <td className="text-end fw-bold">{formatINR((f.total_fare || 0) * r.count)}</td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td colSpan={5} className="fw-bold">Grand Total</td>
                      <td className="text-end fw-bold" style={{ color: ACCENT }}>{formatINR(total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="col-12 col-lg-5">
              <div className="fr-detail-title">Fare Rules</div>
              <div className="fr-detail-text">{fare.fare_rule || "-"}</div>
              <div className="fr-detail-title mt-3">Itinerary</div>
              {(fare.stops || []).map((s) => (
                <div className="fr-detail-text" key={s.stop_number}>
                  {s.departure_airport_code} {formatTime(s.departure_time)}
                  {s.departure_terminal ? ` (${s.departure_terminal})` : ""}
                  <i className="bi bi-arrow-right mx-2"></i>
                  {s.arrival_airport_code} {formatTime(s.arrival_time)}
                  {s.arrival_terminal ? ` (${s.arrival_terminal})` : ""}
                </div>
              ))}
              <div className="fr-muted mt-3">
                Fare Code: <b>{fare.fare_code}</b> &middot; Supplier: <b>{fare.supplier_code}</b>
                <span className="text-capitalize"> ({fare.supplier_type})</span>
              </div>
              <div className="fr-muted">Ref: {fare.fare_reference_code}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlightResultCard;
